import * as _ from 'underscore';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { IGroupBasicInfo } from '../models/IGroupInfo';
import { Module } from '../Module';
import { ActiveDirectoryGroups } from '../resources/ActiveDirectoryGroups';
import { Groups } from '../resources/Groups';
import { ISyncAdGroupCompleteResult } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IResult } from '../ServerApi/types/IResponse';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { LongRunningActionService } from './LongRunningActionService';
import { ModalSize } from './ModalService';
import { ToasterService, ToastType } from './ToasterService';

export let SyncGroupEvent = {
  groupSynced: 'groupSynced'
};

export interface IAddAdGroupDialogScope extends ng.IScope {
  group: {
    model?: IGroupBasicInfo;
    siteRole?: string;
  };
}

export class ActiveDirectoryGroupAction {

  public static $inject = ['$translate',
                           '$q',
                           'ActiveDirectoryGroups',
                           'ConfirmActionDialog',
                           'LongRunningActionService',
                           'ToasterService'];

  // Used for proper translations
  private static Mode = {
    add: 'add',
    sync: 'sync'
  };

  constructor(
    private $translate: ng.translate.ITranslateService,
    private $q: ng.IQService,
    private ActiveDirectoryGroups: ActiveDirectoryGroups,
    private ConfirmActionDialog: ConfirmActionDialog,
    private longRunningActionService: LongRunningActionService,
    private toaster: ToasterService) { }

  public showSyncAdGroupDialog(
    group: IGroupBasicInfo,
    scope: ng.IScope
  ): ng.IPromise<any> {

    const dialogScope = <IAddAdGroupDialogScope> scope.$new();
    dialogScope.group = {
      model: group
    };

    const dialogBody: string = this.$translate.instant('confirmSyncAdGroup', { GROUP_NAME: L10n.elideIfLong(group.name) });

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('synchronize'),
      bodyText: dialogBody,
      actionName: this.$translate.instant('synchronize'),
      action: () => this.ActiveDirectoryGroups.syncAdGroup(group.name, group.domainName)
    });

    return dialogInstance.result
    .then(ResultHelper.rejectResultOnError(this.$q))
    .then((result: IActionResult) => {
      // notification
        this.toaster.pop({
          type: ToastType.Info,
          text: this.$translate.instant('syncAdGroupSubmitted', { GROUP_NAME: L10n.elideIfLong(dialogScope.group.model.name) })
        });
        return result;
      })
      .then(this.longRunningActionService
        .startProgressTrackingForResponse<ISyncAdGroupCompleteResult>())
      .then(this.longRunningActionService.delayResultForSolr())
      .then(this.handleSyncCompleteFor(dialogScope, ActiveDirectoryGroupAction.Mode.sync))
      .catch(this.handleSyncErrorResultFor(dialogScope, ActiveDirectoryGroupAction.Mode.sync))
      .finally(() => dialogScope.$destroy());
  }

  public showSyncAdGroupsDialog(
    selectedGroups: IGroupBasicInfo[],
    scope: ng.IScope
  ): ng.IPromise<any> {

    const groups = _.filter(selectedGroups, Groups.isActiveDirectory);

    if(groups.length === 0) {
      // No active directory groups were selected, display an error message
      return this.ConfirmActionDialog.open({
        scope: scope,
        title: this.$translate.instant('synchronize'),
        bodyText: this.$translate.instant('syncAdGroupsNoGroups')
      }).result;

    } else if(groups.length === 1) {
      // Only 1 active directory group was selected, use the single single AD dialog
      return this.showSyncAdGroupDialog(groups[0], scope);

    } else {

      const dialogInstance = this.ConfirmActionDialog.open({
        scope: scope,
        title: this.$translate.instant('synchronize'),
        bodyText: this.$translate.instant('confirmSyncAdGroups', { GROUP_COUNT: groups.length }, L10n.Interpolations.Count),
        actionName: this.$translate.instant('synchronizeConfirm', { COUNT: groups.length }, L10n.Interpolations.Count),
        action: () => {
          const responsePromise = this.ActiveDirectoryGroups.syncAdGroups(groups);

          // Display results
          responsePromise.then(ResultHelper.rejectResultOnError(this.$q))
            .then((result: IActionResult) => {
              const successMessage = this.$translate.instant('syncAdGroupsSubmitted', { GROUP_COUNT: groups.length }, L10n.Interpolations.Count);
              this.toaster.pop({ type: ToastType.Info, text: successMessage });
              return result;
            })
            .catch((result: IActionResult) => {
              const errorMessage = this.$translate.instant('syncAdGroupsError', { GROUP_COUNT: groups.length }, L10n.Interpolations.Count);
              this.toaster.pop({ type: ToastType.Error, text: errorMessage });
            });

          return responsePromise;
        }
      });

      return dialogInstance.result;
    }
  }

  public showAddAdGroupDialog(scope: ng.IScope): ng.IPromise<any> {

    const dialogScope = <IAddAdGroupDialogScope> scope.$new();
    dialogScope.group = { siteRole: SiteRoles.Explorer };

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      title: this.$translate.instant('addUsersImportAdGroupTitle'),
      bodyTemplateUrl: 'addActiveDirectoryGroupDialog.html',
      actionName: this.$translate.instant('addUsersImportAdGroupSubmit'),
      action: () => {
        const group = dialogScope.group.model;
        const siteRole = dialogScope.group.siteRole || SiteRoles.Unlicensed;
        return this.ActiveDirectoryGroups.syncAdGroup(group.name, group.domainName, siteRole);
      },
      position: { reserveHeight: 450 },
      size: ModalSize.fitToContent,
      confirmDisabled: () => !dialogScope.group.model || !dialogScope.group.siteRole // TODO: Replace with form validation
    });
    return dialogInstance.result
      .then(ResultHelper.rejectResultOnError(this.$q))
      .then((result: IActionResult) => {
        this.toaster.pop({
          type: ToastType.Info,
          text: this.$translate.instant('addAdGroupSubmitted', {GROUP_NAME: L10n.elideIfLong(dialogScope.group.model.name)})
        });
        return result;
      })
      .then(this.longRunningActionService
        .startProgressTrackingForResponse<ISyncAdGroupCompleteResult>())
      .then(this.longRunningActionService.delayResultForSolr())
      .then(this.handleSyncCompleteFor(dialogScope, ActiveDirectoryGroupAction.Mode.add))
      .catch(this.handleSyncErrorResultFor(dialogScope, ActiveDirectoryGroupAction.Mode.add))
      .finally(() => dialogScope.$destroy());
  }

  private handleSyncErrorResultFor(scope: IAddAdGroupDialogScope, actionType: string) {
    return (result?: IResult) => {
      if(!result || !result.errors) return; // Must have failed for some reason with out an error code, like cancel

      const group = scope.group.model;
      const errorStringArgs = {
        GROUP_NAME: L10n.elideIfLong(group.name),
        DOMAIN_NAME: L10n.elideIfLong(group.domainName)
      };
      if (result.errors[0].code === CommonErrorCode.AdGroupNotFound) {
        // ad group not found is the only thing we expect to handle at the moment
        this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant('errorActiveDirectoryGroupNotFound', errorStringArgs)});
      } else {
        this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant(actionType + 'AdGroupError', errorStringArgs)});
      }

      return result; // For chaining
    };
  }

  private handleSyncCompleteFor(scope: IAddAdGroupDialogScope, actionType: string) {
    return (result: ISyncAdGroupCompleteResult) => {

      // Add a delay to allow solr to index
      scope.$emit(SyncGroupEvent.groupSynced);

      // Create success messages

      const completeMessageStringArgs = {
        GROUP_NAME: L10n.elideIfLong(scope.group.model.name),
        DOMAIN_NAME: L10n.elideIfLong(scope.group.model.domainName)
      };

      const successLines = [
        this.$translate.instant(actionType + 'AdGroupSuccess', completeMessageStringArgs),
        this.$translate.instant('syncAdGroupSuccessSummary', {
          NUM_ADDED: result.usersAddedToGroup || 0,
          NUM_REMOVED: result.usersRemovedFromGroup || 0
        }, L10n.Interpolations.Count)
      ];

      if(result.usersAddedToSite > 0) {
        successLines.push(
          this.$translate.instant('syncAdGroupSuccessNumAddedToSite',
            { NUM_ADDED: result.usersAddedToSite },
            L10n.Interpolations.Count));
      }
      if(result.usersSiteRoleUpdated > 0) {
        successLines.push(
          this.$translate.instant('syncAdGroupSuccessNumSiteRoleUpdated',
            { NUM_UPDATED: result.usersSiteRoleUpdated },
            L10n.Interpolations.Count));
      }

      this.toaster.pop({
        type: ToastType.Info,
        text: successLines.join('\n'),
        timeoutDelay: ToasterService.NoTimeout
      });

      // Create error messages

      const errorLines = [
        this.$translate.instant(actionType + 'AdGroupError', completeMessageStringArgs)
      ];
      if(result.usersWithInsufficientLicenses > 0) {
        errorLines.push(
          this.$translate.instant('syncAdGroupErrorInsufficientLicenses',
            { COUNT: result.usersWithInsufficientLicenses },
            L10n.Interpolations.Count));
      }
      if(result.usersSkipped > 0) {
        errorLines.push(
          this.$translate.instant('syncAdGroupErrorNumUsersSkipped',
            { COUNT: result.usersWithInsufficientLicenses },
            L10n.Interpolations.Count));
      }
      if(errorLines.length > 1) { // Errors actually occurred if there is more than the default error message
        this.toaster.pop({ type: ToastType.Error, text: errorLines.join('\n')});
      }

      return result; // For Chaining
    };
  }

}

Module.instance().service('ActiveDirectoryGroupAction', ActiveDirectoryGroupAction);
